import { useState, useEffect, useCallback, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch, useApi, getToken } from '../hooks/useApi.js';
import { uiConfirm, uiPrompt } from '../utils/dialogs.js';
import { FulfilmentOrderDetail, EmailQueueView, CreateFulfilmentOrderModal } from './Fulfilment.jsx';
import { OrderFilesView } from './OrderImports.jsx';
import { OrderFormsView } from './OrderForms.jsx';
import { OrderDetail as SalesOrderDetail, CreateOrderModal } from './SalesOrders.jsx';

/**
 * Orders — everything going out in one place: fulfilment orders, own-brand
 * sales, sample parcels and pallet consignments. Tick what's ready and start
 * a despatch run: one picking list, one DPD file, a tick per order as it's
 * packed, then despatch. Several runs a day are fine.
 */

const KIND = {
  fulfilment: { label: 'Fulfilment', bg: '#e6eefc', fg: '#1d3f8a' },
  sales: { label: 'Own brand', bg: '#e7f1e2', fg: '#2f5a2a' },
  sample: { label: 'Sample', bg: '#f3e8fb', fg: '#5b2b84' },
  consignment: { label: 'Pallets', bg: '#fff1dc', fg: '#7a4a00' },
};
const STAGES = [['confirm', 'To confirm'], ['ready', 'Ready to pick'], ['run', 'In a run'], ['done', 'Despatched']];
const KINDS = [['', 'All'], ['fulfilment', 'Fulfilment'], ['sales', 'Own brand'], ['sample', 'Samples'], ['consignment', 'Pallets']];
const STAGE_CHIP = { confirm: ['#fff4d6', '#7a4a00'], ready: ['#eef1e8', '#22391f'], run: ['#e6eefc', '#1d3f8a'], done: ['#e7f1e2', '#2f6b2a'] };

function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const styles = {
    primary: { background: 'var(--pld-green)', color: '#fff', border: 'none' },
    ghost: { background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)' },
    danger: { background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' },
  };
  return <button {...props} style={{ padding: size === 'sm' ? '6px 12px' : '9px 18px', fontSize: size === 'sm' ? 13 : 14, fontWeight: 600, borderRadius: 8, cursor: props.disabled ? 'not-allowed' : 'pointer', opacity: props.disabled ? 0.55 : 1, whiteSpace: 'nowrap', ...styles[variant], ...props.style }}>{children}</button>;
}
const Chip = ({ bg, fg, children }) => <span style={{ display: 'inline-block', fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: bg, color: fg, whiteSpace: 'nowrap' }}>{children}</span>;
const KindChip = ({ kind }) => <Chip bg={KIND[kind]?.bg} fg={KIND[kind]?.fg}>{KIND[kind]?.label || kind}</Chip>;
const Err = ({ msg }) => (msg ? <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, padding: '10px 14px', fontSize: 13, margin: '0 0 14px' }}>{msg}</div> : null);
const timeOf = (d) => (d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '');
// A run from an earlier day gets its date shown (run numbers start again each day).
const isToday = (d) => d && new Date(d).toDateString() === new Date().toDateString();
const dayOf = (d) => new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

/** Fetch a run document with the login token; PDFs open in a new tab, the CSV downloads. */
async function getDoc(path, { pdf }) {
  const win = pdf ? window.open('', '_blank') : null;
  const res = await fetch(`/api${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) {
    if (win) win.close();
    const b = await res.json().catch(() => ({}));
    throw new Error(b.error || `Couldn't make it (HTTP ${res.status})`);
  }
  const url = URL.createObjectURL(await res.blob());
  let notes = [];
  try { notes = JSON.parse(decodeURIComponent(res.headers.get('X-Blake-Notes') || '%5B%5D')); } catch { /* none */ }
  if (pdf) { if (win) win.location.href = url; else window.open(url, '_blank'); } else {
    const name = ((res.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/) || [])[1] || 'dpd-import.csv';
    const a = document.createElement('a'); a.href = url; a.download = name; a.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return { count: Number(res.headers.get('X-Blake-Count') || 0), notes };
}

// ── Shopify orders that didn't come in ──────────────────────────────────────

function ShopifyProblems({ onFixed }) {
  const { data, refetch } = useApi('/despatch/shopify-problems');
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState({});
  const rows = Array.isArray(data) ? data : [];
  if (!rows.length) return null;
  const go = async (r, action) => {
    setBusy(r.id); setMsg((m) => ({ ...m, [r.id]: null }));
    try {
      const res = await apiFetch(`/despatch/shopify-problems/${r.id}/${action}`, { method: 'POST' });
      const d = res.data ?? res;
      if (action === 'retry') setMsg((m) => ({ ...m, [r.id]: d.status === 'processed' ? `✓ ${d.message}` : d.message }));
      refetch(); onFixed?.();
    } catch (e) { setMsg((m) => ({ ...m, [r.id]: e.message })); } finally { setBusy(null); }
  };
  return (
    <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
      <div style={{ fontWeight: 700, color: '#9a3412', marginBottom: 4 }}>{rows.length} Shopify order{rows.length === 1 ? '' : 's'} couldn't come in</div>
      <div style={{ fontSize: 13, color: '#9a3412', marginBottom: 8 }}>Usually a product whose Shopify SKU isn't on a stock item. Put the SKU on the stock item (as its SKU or legacy SKU), then Try again.</div>
      {rows.map((r) => (
        <div key={r.id} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '6px 0', borderTop: '1px solid #fed7aa' }}>
          <strong style={{ fontSize: 13 }}>{r.order_name || r.shopify_order_id}</strong>
          <span style={{ fontSize: 13, flex: 1, minWidth: 240 }}>{r.process_error}{msg[r.id] ? <span style={{ display: 'block', color: msg[r.id].startsWith('✓') ? '#166534' : '#b91c1c' }}>{msg[r.id]}</span> : null}</span>
          <Btn size="sm" disabled={busy === r.id} onClick={() => go(r, 'retry')}>Try again</Btn>
          <Btn size="sm" variant="ghost" disabled={busy === r.id} onClick={() => go(r, 'dismiss')}>Ignore</Btn>
        </div>
      ))}
    </div>
  );
}

// ── Shopify orders Blake hasn't got (Joe, 5 Nov 2026) ─────────────────────────
// Asks Shopify for its recent orders and lists any not in Blake, with why.

function ShopifyCheck({ onFixed }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState({});
  const check = async () => {
    setBusy('check'); setErr(null);
    try { const r = await apiFetch('/despatch/shopify-missing?days=14'); setRes(r.data ?? r); } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };
  const bring = async (m) => {
    setBusy(m.id); setMsg((x) => ({ ...x, [m.id]: null }));
    try {
      const r = await apiFetch(`/despatch/shopify-missing/${m.id}/bring-in`, { method: 'POST' });
      const d = r.data ?? r;
      setMsg((x) => ({ ...x, [m.id]: d.status === 'processed' ? `✓ ${d.message}` : d.message }));
      onFixed?.();
    } catch (e) { setMsg((x) => ({ ...x, [m.id]: e.message })); } finally { setBusy(null); }
  };
  const box = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13 };
  if (!res && !err) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <button type="button" onClick={check} disabled={!!busy} style={{ background: 'none', border: 'none', color: 'var(--pld-green)', cursor: 'pointer', fontSize: 13, padding: 0 }}>
          {busy ? 'Asking Shopify…' : 'Shopify order missing? Check Shopify'}
        </button>
      </div>
    );
  }
  return (
    <div style={box}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
        <strong>{err ? "Couldn't check Shopify" : res.missing.length ? `${res.missing.length} Shopify order${res.missing.length === 1 ? '' : 's'} not in Blake` : `All ${res.checked} Shopify orders since ${new Date(res.since).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} are in Blake`}</strong>
        <span style={{ display: 'flex', gap: 12 }}>
          <button type="button" onClick={check} disabled={!!busy} style={{ background: 'none', border: 'none', color: 'var(--pld-green)', cursor: 'pointer', fontSize: 13, padding: 0 }}>{busy === 'check' ? 'Checking…' : 'Check again'}</button>
          <button type="button" onClick={() => { setRes(null); setErr(null); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, padding: 0 }}>Close</button>
        </span>
      </div>
      {err && <div style={{ color: '#b91c1c', marginTop: 6 }}>{err}</div>}
      {res?.missing.map((m) => (
        <div key={m.id} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '6px 0', borderTop: '1px solid var(--border)', marginTop: 6 }}>
          <strong>{m.name}</strong>
          <span style={{ color: 'var(--text-muted)' }}>{new Date(m.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}{m.customer ? ` · ${m.customer}` : ''}{m.total ? ` · £${Number(m.total).toFixed(2)}` : ''}</span>
          <span style={{ flex: 1, minWidth: 200, color: m.ready ? '#166534' : '#9a3412' }}>
            {m.why}
            {msg[m.id] && <span style={{ display: 'block', color: msg[m.id].startsWith('✓') ? '#166534' : '#b91c1c' }}>{msg[m.id]}</span>}
          </span>
          {!msg[m.id]?.startsWith('✓') && (m.ready || /Can't match/.test(m.why) || /fulfilled/.test(m.why)) && (
            <Btn size="sm" variant={m.ready ? undefined : 'ghost'} disabled={busy === m.id} onClick={() => bring(m)}>{busy === m.id ? 'Bringing in…' : 'Bring in'}</Btn>
          )}
        </div>
      ))}
    </div>
  );
}

// ── The list ────────────────────────────────────────────────────────────────

const opens = (r) => r.kind === 'fulfilment' || r.kind === 'sales';

function OrdersList({ onOpenRun, onOpenOrder }) {
  const [stage, setStage] = useState('ready');
  const [kind, setKind] = useState('');
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);
  const [creating, setCreating] = useState(false);
  const [creatingFo, setCreatingFo] = useState(false);
  const { data, loading, refetch } = useApi('/despatch/orders');
  const all = data?.rows || [];
  const counts = data?.counts || {};
  const runs = data?.runs || [];
  const openRuns = runs.filter((r) => r.status === 'open' && isToday(r.run_date)); // add only to today's runs
  const rows = all.filter((r) => r.stage === stage && (!kind || r.kind === kind));
  const pickedRows = all.filter((r) => picked[`${r.kind}:${r.id}`]);
  const canPick = stage === 'confirm' || stage === 'ready';

  useEffect(() => { setPicked({}); }, [stage]);
  const key = (r) => `${r.kind}:${r.id}`;
  const toggle = (r) => setPicked((p) => { const n = { ...p }; if (n[key(r)]) delete n[key(r)]; else n[key(r)] = true; return n; });
  const allOn = rows.length > 0 && rows.every((r) => picked[key(r)]);
  const toggleAll = () => setPicked(allOn ? {} : Object.fromEntries(rows.map((r) => [key(r), true])));
  const items = pickedRows.map((r) => ({ kind: r.kind, id: r.id }));

  const WORD = { fulfilment: ['fulfilment order', 'fulfilment orders'], sales: ['own-brand order', 'own-brand orders'], sample: ['sample parcel', 'sample parcels'], consignment: ['pallet consignment', 'pallet consignments'] };
  const summary = Object.entries(pickedRows.reduce((m, r) => { m[r.kind] = (m[r.kind] || 0) + 1; return m; }, {}))
    .map(([k, n]) => `${n} ${WORD[k][n === 1 ? 0 : 1]}`).join(' · ');
  const dpdParcels = pickedRows.filter((r) => r.how === 'DPD').reduce((t, r) => t + (r.parcels || 1), 0);

  async function act(fn) {
    setBusy(true); setErr(null); setInfo(null);
    try { await fn(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  const startRun = () => act(async () => {
    const run = await apiFetch('/despatch/runs', { method: 'POST', body: { items } });
    setPicked({}); onOpenRun((run.data ?? run).id);
  });
  const addTo = (run) => act(async () => {
    await apiFetch(`/despatch/runs/${run.id}/items`, { method: 'POST', body: { items } });
    setPicked({}); onOpenRun(run.id);
  });
  const confirmPicked = () => act(async () => {
    const r = await apiFetch('/despatch/orders/confirm', { method: 'POST', body: { items } });
    const d = r.data ?? r;
    setPicked({}); refetch();
    if (d.failed) setErr(d.results.filter((x) => !x.ok).map((x) => `${all.find((o) => o.id === x.id)?.ref || 'An order'}: ${x.error}`).join(' · '));
    else setInfo(`${d.done} confirmed — stock reserved. They're on Ready to pick now.`);
  });
  const cancelPicked = () => act(async () => {
    const reason = await uiPrompt(`Cancel ${pickedRows.length} order${pickedRows.length === 1 ? '' : 's'}? Give a reason.`, '', { okLabel: 'Cancel them' });
    if (reason === null || reason === undefined) return;
    const r = await apiFetch('/despatch/orders/cancel', { method: 'POST', body: { items, reason: reason || 'Cancelled' } });
    const d = r.data ?? r;
    setPicked({}); refetch();
    if (d.failed) setErr(d.results.filter((x) => !x.ok).map((x) => x.error).join(' · '));
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -6, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="ghost" onClick={() => setCreatingFo(true)}>+ New fulfilment order</Btn>
          <Btn onClick={() => setCreating(true)}>+ New sales order</Btn>
        </div>
      </div>

      <ShopifyProblems onFixed={refetch} />
      <ShopifyCheck onFixed={refetch} />

      {runs.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em', color: 'var(--text-muted)' }}>DESPATCH RUNS</span>
          {runs.map((r) => {
            const old = !isToday(r.run_date);
            return (
              <button key={r.id} onClick={() => onOpenRun(r.id)} title={old ? 'Still open from an earlier day — open it to despatch or cancel it' : undefined}
                style={{ border: `1px solid ${old ? '#f59e0b' : 'var(--border)'}`, background: old ? '#fef3c7' : r.status === 'open' ? '#e6eefc' : 'var(--surface)', color: old ? '#92400e' : r.status === 'open' ? '#1d3f8a' : 'var(--text)', borderRadius: 999, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Run {r.run_number} · {old ? dayOf(r.run_date) : timeOf(r.created_at)} · {r.orders} order{r.orders === 1 ? '' : 's'} · {r.status === 'open' ? (old ? 'still open' : 'in progress') : `despatched ${timeOf(r.despatched_at)}`}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        {STAGES.map(([k, label]) => {
          const on = stage === k;
          return (
            <button key={k} onClick={() => setStage(k)} style={{ height: 38, padding: '0 14px', borderRadius: 999, fontSize: 14, fontWeight: 600, cursor: 'pointer', border: on ? '1px solid var(--pld-green)' : '1px solid var(--border)', background: on ? 'var(--pld-green)' : 'var(--surface)', color: on ? '#fff' : 'var(--text)' }}>
              {label}<span style={{ marginLeft: 6, fontSize: 12, padding: '1px 7px', borderRadius: 999, background: on ? 'rgba(255,255,255,.2)' : 'var(--surface-2)' }}>{counts[k] || 0}</span>
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {KINDS.map(([k, label]) => (
          <button key={k || 'all'} onClick={() => setKind(k)} style={{ padding: '4px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--border)', background: kind === k ? 'var(--surface-2)' : 'transparent', color: kind === k ? 'var(--text)' : 'var(--text-muted)' }}>{label}</button>
        ))}
      </div>

      <Err msg={err} />
      {info && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>{info}</div>}

      {canPick && pickedRows.length > 0 && (
        <div style={{ position: 'sticky', top: 0, zIndex: 5, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#22391f', color: '#fff', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
          <strong>{pickedRows.length} selected</strong>
          <span style={{ fontSize: 13, opacity: 0.85 }}>{summary}{stage === 'ready' && dpdParcels ? ` · ${dpdParcels} DPD parcel${dpdParcels === 1 ? '' : 's'}` : ''}</span>
          <span style={{ flex: 1 }} />
          <Btn variant="ghost" size="sm" onClick={() => setPicked({})}>Clear</Btn>
          {stage === 'confirm' && <Btn variant="danger" size="sm" disabled={busy} onClick={cancelPicked}>Cancel orders</Btn>}
          {stage === 'confirm' && <Btn size="sm" disabled={busy} onClick={confirmPicked} style={{ background: '#fff', color: '#22391f' }}>Confirm</Btn>}
          {stage === 'ready' && openRuns.map((r) => <Btn key={r.id} variant="ghost" size="sm" disabled={busy} onClick={() => addTo(r)}>Add to run {r.run_number}</Btn>)}
          {stage === 'ready' && <Btn size="sm" disabled={busy} onClick={startRun} style={{ background: '#fff', color: '#22391f' }}>Start despatch run</Btn>}
        </div>
      )}

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <style>{'.order-row:hover { background: var(--surface-2) !important; }'}</style>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--surface-2)', textAlign: 'left', fontSize: 12, color: 'var(--text-muted)', letterSpacing: '.04em' }}>
              {canPick && <th style={{ padding: '10px 12px', width: 30 }}><input type="checkbox" checked={allOn} onChange={toggleAll} /></th>}
              <th style={{ padding: '10px 12px' }}>KIND</th><th style={{ padding: '10px 12px' }}>WHO</th><th style={{ padding: '10px 12px' }}>WHAT</th>
              <th style={{ padding: '10px 12px' }}>HOW IT GOES</th><th style={{ padding: '10px 12px' }}>STATUS</th>
            </tr>
          </thead>
          <tbody>
            {loading && !data && <tr><td colSpan={6} style={{ padding: 24, color: 'var(--text-muted)' }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} style={{ padding: 24, color: 'var(--text-muted)' }}>Nothing here.</td></tr>}
            {rows.map((r) => (
              <tr key={key(r)} className={opens(r) ? 'order-row' : undefined}
                // The whole row opens the order (Joe, 27 Oct 2026) — not the tick box or the run chip.
                onClick={(e) => { if (opens(r) && !e.target.closest('input, button, a, label')) onOpenOrder(r); }}
                style={{ borderTop: '1px solid var(--border)', background: picked[key(r)] ? '#f3f7ee' : undefined, cursor: opens(r) ? 'pointer' : undefined }}>
                {canPick && <td style={{ padding: '10px 12px' }}><input type="checkbox" checked={!!picked[key(r)]} onChange={() => toggle(r)} /></td>}
                <td style={{ padding: '10px 12px' }}><KindChip kind={r.kind} /></td>
                <td style={{ padding: '10px 12px' }}>
                  <div style={{ fontWeight: 600 }}>{r.who}{r.where ? <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>, {r.where}</span> : null}</div>
                  <div style={{ fontSize: 13, color: opens(r) ? 'var(--pld-green)' : 'var(--text-muted)' }}>{r.ref}</div>
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <div>{r.what}</div>
                  {r.what_sub && <div style={{ fontSize: 12, color: /check/.test(r.what_sub) ? '#b45309' : 'var(--text-muted)' }}>{r.what_sub}</div>}
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <div style={{ fontWeight: 600 }}>{r.how}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.stage === 'done' && r.sent_in ? `Run ${r.sent_in.number} · ${new Date(r.dispatched_at || r.sent_in.date).toLocaleDateString('en-GB')}` : r.how_sub}</div>
                </td>
                <td style={{ padding: '10px 12px' }}>
                  {r.stage === 'run'
                    ? <button onClick={() => onOpenRun(r.run.id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}><Chip bg={STAGE_CHIP.run[0]} fg={STAGE_CHIP.run[1]}>In run {r.run.number}{!isToday(r.run.date) ? ` (${dayOf(r.run.date)})` : ''}{r.run.packed ? ' · packed' : ''}</Chip></button>
                    : <Chip bg={STAGE_CHIP[r.stage][0]} fg={STAGE_CHIP[r.stage][1]}>{STAGES.find(([k]) => k === r.stage)[1]}</Chip>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {creatingFo && <CreateFulfilmentOrderModal onClose={() => setCreatingFo(false)} onSaved={() => { setCreatingFo(false); refetch(); setStage('confirm'); }} />}
      {creating && <CreateOrderModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); refetch(); setStage('confirm'); }} />}
    </div>
  );
}

// ── A despatch run ──────────────────────────────────────────────────────────

const GROUPS = [
  ['DPD', 'Going by DPD', 'In the DPD file'],
  ['Collection', 'Being collected', 'Hand over when they arrive'],
  ['Our van', 'Our van', 'Delivered by us'],
  ['Pallet', 'Pallets', 'Haulier collects — manifest goes with them'],
];

// Put an order's delivery details right from the DPD check (Joe, 5 Nov 2026).
function FixAddress({ runId, x, onCancel, onSaved }) {
  const [f, setF] = useState({ ...x.edit });
  const [err, setErr] = useState(''); const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));
  const box = { padding: '7px 9px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, width: '100%', boxSizing: 'border-box', background: 'var(--surface)', color: 'var(--text)' };
  const F = (k, label, props = {}) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, fontWeight: 600, color: 'var(--text-muted)' }}>{label}
      <input style={box} value={f[k]} onChange={set(k)} {...props} />
    </label>
  );
  async function save(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try { await apiFetch(`/despatch/runs/${runId}/address/${x.order_kind}/${x.order_id}`, { method: 'PATCH', body: f }); await onSaved(); }
    catch (ex) { setErr(ex.message); setSaving(false); }
  }
  return (
    <form onSubmit={save}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Delivery details for {x.ref} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>— saved on the order</span></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
        {F('name', 'Name')}{F('company', 'Company')}
        {F('line1', 'Address')}{F('line2', 'Address line 2')}
        {F('city', 'Town')}{F('postcode', 'Postcode')}
        {F('phone', 'Phone', { type: 'tel' })}{F('email', 'Email', { type: 'email' })}
      </div>
      {err && <div style={{ color: '#b91c1c', fontSize: 12.5, marginTop: 6 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
        <Btn size="sm" variant="ghost" type="button" onClick={onCancel}>Cancel</Btn>
        <Btn size="sm" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save and check again'}</Btn>
      </div>
    </form>
  );
}

// ── Check the DPD file before it goes (Joe, 27 Oct 2026) ─────────────────────
// Every shipment with its weight, parcels and contact details; anything DPD
// would reject in red, anything worth a look in amber.
function DpdCheck({ runId, onClose, onDownload }) {
  const [data, setData] = useState(null); const [err, setErr] = useState('');
  const load = () => apiFetch(`/despatch/runs/${runId}/dpd-check`).then((r) => setData(r.data ?? r)).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, [runId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [fixing, setFixing] = useState(null); // index of the row being put right
  const bad = data ? data.shipments.filter((x) => x.problems.length).length : 0;
  const warn = data ? data.shipments.filter((x) => !x.problems.length && x.warnings.length).length : 0;
  const th = { textAlign: 'left', padding: '7px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', verticalAlign: 'top', fontSize: 13 };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 400, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, width: 'min(1100px, 100%)', padding: 20, boxShadow: '0 10px 40px rgba(0,0,0,.25)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Check the DPD file</h2>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text-muted)' }}>✕</button>
        </div>
        {err && <Err msg={err} />}
        {!data && !err && <p style={{ color: 'var(--text-muted)' }}>Checking…</p>}
        {data && (<>
          <p style={{ margin: '0 0 12px', fontSize: 14 }}>
            <strong>{data.totals.shipments}</strong> shipment{data.totals.shipments === 1 ? '' : 's'} · <strong>{data.totals.parcels}</strong> parcel{data.totals.parcels === 1 ? '' : 's'} · <strong>{data.totals.weight.toFixed(2)} kg</strong>
            {bad > 0 && <span style={{ color: '#9b2c1a', fontWeight: 600 }}> · {bad} need fixing</span>}
            {warn > 0 && <span style={{ color: '#7a4a00', fontWeight: 600 }}> · {warn} to look at</span>}
            {!bad && !warn && <span style={{ color: '#2f6b2a', fontWeight: 600 }}> · all look fine</span>}
          </p>
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--surface-2)' }}>{['Ref', 'Deliver to', 'Postcode', 'Parcels', 'Weight', 'Contact', 'Check'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {data.shipments.map((x, i) => (<Fragment key={i}>
                  <tr style={{ borderTop: '1px solid var(--border)', background: x.problems.length ? '#fde8e4' : x.warnings.length ? '#fff7e6' : 'transparent' }}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}><strong>{x.ref}</strong><div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{x.kind}{x.saturday ? ' · Saturday' : ''}</div></td>
                    <td style={td}>{x.company || x.name}{x.company && x.name ? <div style={{ fontSize: 12 }}>{x.name}</div> : null}<div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{x.address}</div></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{x.postcode || '—'}{x.country !== 'GB' ? ` (${x.country})` : ''}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{x.parcels}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>{x.weight.toFixed(2)} kg{x.parcels > 1 && <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{x.per_parcel.toFixed(2)} kg each</div>}</td>
                    <td style={{ ...td, fontSize: 12 }}>{x.phone || <span style={{ color: 'var(--text-muted)' }}>no phone</span>}<div style={{ color: 'var(--text-muted)', wordBreak: 'break-all' }}>{x.email || 'no email'}</div></td>
                    <td style={{ ...td, fontSize: 12, minWidth: 180 }}>
                      {x.problems.map((m) => <div key={m} style={{ color: '#9b2c1a', fontWeight: 600 }}>✕ {m}</div>)}
                      {x.warnings.map((m) => <div key={m} style={{ color: '#7a4a00' }}>! {m}</div>)}
                      {!x.problems.length && !x.warnings.length && <span style={{ color: '#2f6b2a' }}>✓</span>}
                      {x.edit && fixing !== i && (
                        <button type="button" onClick={() => setFixing(i)} style={{ display: 'block', marginTop: 4, background: 'none', border: 'none', padding: 0, color: 'var(--pld-green)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                          {x.problems.length || x.warnings.some((w) => /phone|email|town/i.test(w)) ? 'Fix address' : 'Change address'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {fixing === i && (
                    <tr style={{ background: 'var(--surface-2)' }}>
                      <td colSpan={7} style={{ padding: 12 }}>
                        <FixAddress runId={runId} x={x} onCancel={() => setFixing(null)} onSaved={async () => { await load(); setFixing(null); }} />
                      </td>
                    </tr>
                  )}
                </Fragment>))}
              </tbody>
            </table>
          </div>
          {data.notes?.map((n) => <p key={n} style={{ fontSize: 12.5, color: '#7a4a00', margin: '8px 0 0' }}>{n}</p>)}
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '10px 0 0' }}>Put addresses and contacts right here with Fix address — it changes the order. Shipping weights are on the product (Stock → item). Weights shown are what goes in the file.</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
            <Btn variant="ghost" onClick={onClose}>Back</Btn>
            <Btn onClick={onDownload}>{bad ? `Download anyway (${bad} to fix)` : 'Download DPD file'}</Btn>
          </div>
        </>)}
      </div>
    </div>
  );
}

function RunView({ runId, onBack }) {
  const { data, loading, error, refetch } = useApi(`/despatch/runs/${runId}`);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [docMsg, setDocMsg] = useState({});
  const [checkingDpd, setCheckingDpd] = useState(false);
  if (loading && !data) return <p style={{ color: 'var(--text-muted)' }}>Loading…</p>;
  if (error && !data) return <Err msg={error} />;
  const { run, orders, docs } = data;
  const open = run.status === 'open';
  const left = orders.filter((o) => !o.despatched);
  const unpacked = left.filter((o) => !o.packed);

  async function act(fn) { setBusy(true); setErr(null); try { await fn(); } catch (e) { setErr(e.message); } finally { setBusy(false); refetch(); } }
  const doc = (k, path, pdf, ok) => act(async () => {
    const r = await getDoc(path, { pdf });
    setDocMsg((m) => ({ ...m, [k]: { text: ok(r), notes: r.notes } }));
  });
  const pack = (o) => act(() => apiFetch(`/despatch/runs/${runId}/items/${o.item_id}/packed`, { method: 'POST', body: { packed: !o.packed } }));
  const saturday = (o) => act(() => apiFetch(`/despatch/runs/${runId}/items/${o.item_id}/saturday`, { method: 'POST', body: { saturday: !o.saturday } }));
  const takeOut = (o) => act(() => apiFetch(`/despatch/runs/${runId}/items/${o.item_id}`, { method: 'DELETE' }));
  const despatch = () => act(async () => {
    if (!(await uiConfirm(`Despatch all ${left.length} order${left.length === 1 ? '' : 's'} in Run ${run.run_number}? Stock comes off, duty and billing are done as each one goes.`, { okLabel: 'Despatch' }))) return;
    const r = await apiFetch(`/despatch/runs/${runId}/despatch`, { method: 'POST' });
    const d = r.data ?? r;
    if (d.failed) setErr(`${d.failed} couldn't be despatched — see the red notes below. The rest have gone.`);
  });
  const cancelRun = () => act(async () => {
    if (!(await uiConfirm(`Cancel Run ${run.run_number}? Everything in it goes back to Ready to pick.`, { okLabel: 'Cancel run', danger: true }))) return;
    await apiFetch(`/despatch/runs/${runId}/cancel`, { method: 'POST' });
    onBack();
  });

  const cards = [
    { k: 'pick', title: 'Picking list', text: `${orders.length} order${orders.length === 1 ? '' : 's'} on one list, with where to pick from`, btn: run.picking_list_at ? 'Print again' : 'Print picking list', done: run.picking_list_at,
      go: () => doc('pick', `/despatch/runs/${runId}/picking-list.pdf`, true, () => 'Opened in a new tab') },
    docs.dpd_orders > 0 && { k: 'dpd', title: 'DPD file', text: `${docs.dpd_orders} shipment${docs.dpd_orders === 1 ? '' : 's'} · ${docs.dpd_parcels} parcel${docs.dpd_parcels === 1 ? '' : 's'}${docs.saturday ? ` · ${docs.saturday} Saturday delivery` : ''} — upload in MyDPD Local`, btn: run.dpd_file_at ? 'Check & download again' : 'Check & download', done: run.dpd_file_at,
      go: () => setCheckingDpd(true) },
    (docs.packing_slips > 0 || docs.own_slips > 0) && { k: 'slips', title: 'Packing slips', text: [docs.packing_slips > 0 && `${docs.packing_slips} client slip${docs.packing_slips === 1 ? '' : 's'}`, docs.own_slips > 0 && `${docs.own_slips} of ours`].filter(Boolean).join(' + ') + ' to go in the boxes', btn: 'Print packing slips',
      go: () => doc('slips', `/despatch/runs/${runId}/packing-slips.pdf`, true, (r) => `${r.count} opened in a new tab`) },
    ...docs.manifests.map((m) => ({ k: `m-${m.id}`, title: `Manifest ${m.consignment_ref}`, text: m.title || 'Pallet manifest for the haulier', btn: 'Print manifest',
      go: () => doc(`m-${m.id}`, `/consignments/${m.id}/manifest.pdf`, true, () => 'Opened in a new tab') })),
  ].filter(Boolean);

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--pld-green)', cursor: 'pointer', padding: 0, fontSize: 14, marginBottom: 10 }}>← Orders</button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em', color: 'var(--text-muted)' }}>DESPATCH RUN</div>
          <h1 style={{ margin: '2px 0 0', fontSize: 24, fontWeight: 700 }}>Run {run.run_number} · {new Date(run.run_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} {timeOf(run.created_at)}</h1>
        </div>
        <Chip bg={open ? '#e6eefc' : run.status === 'despatched' ? '#e7f1e2' : '#f3f4f6'} fg={open ? '#1d3f8a' : run.status === 'despatched' ? '#2f6b2a' : '#6b7280'}>
          {open ? `${left.length - unpacked.length} of ${left.length} packed` : run.status === 'despatched' ? `Despatched ${timeOf(run.despatched_at)}` : 'Cancelled'}
        </Chip>
        <span style={{ flex: 1 }} />
        {open && !orders.some((o) => o.despatched) && <Btn variant="ghost" size="sm" disabled={busy} onClick={cancelRun}>Cancel run</Btn>}
      </div>

      <Err msg={err} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 22 }}>
        {checkingDpd && <DpdCheck runId={runId} onClose={() => setCheckingDpd(false)}
          onDownload={() => { setCheckingDpd(false); doc('dpd', `/despatch/runs/${runId}/dpd-file.csv`, false, (r) => `${r.count} shipment${r.count === 1 ? '' : 's'} downloaded`); }} />}
        {cards.map((c) => (
          <div key={c.k} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontWeight: 700 }}>{c.title}{c.done ? <span style={{ marginLeft: 6, fontSize: 12, color: '#2f6b2a', fontWeight: 600 }}>✓ {timeOf(c.done)}</span> : null}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', flex: 1 }}>{c.text}</div>
            <Btn size="sm" variant={c.done ? 'ghost' : 'primary'} disabled={busy} onClick={c.go}>{c.btn}</Btn>
            {docMsg[c.k] && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{docMsg[c.k].text}{docMsg[c.k].notes?.map((n) => <div key={n} style={{ color: '#b45309' }}>{n}</div>)}</div>}
          </div>
        ))}
      </div>

      {GROUPS.map(([how, title, sub]) => {
        const list = orders.filter((o) => o.how === how);
        if (!list.length) return null;
        return (
          <div key={how} style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>{title}</h3>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{list.length} · {sub}</span>
            </div>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12 }}>
              {list.map((o, i) => (
                <div key={o.item_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderTop: i ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: open && !o.despatched ? 'pointer' : 'default', minWidth: 100 }}>
                    <input type="checkbox" checked={o.packed || o.despatched} disabled={!open || o.despatched || busy} onChange={() => pack(o)} style={{ width: 18, height: 18 }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: o.despatched ? '#2f6b2a' : o.packed ? 'var(--text)' : 'var(--text-muted)' }}>{o.despatched ? 'Gone' : o.packed ? 'Packed' : 'To pack'}</span>
                  </label>
                  <KindChip kind={o.kind} />
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 600 }}>{o.who}{o.where ? <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>, {o.where}</span> : null}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{o.ref} · {o.what}</div>
                    {o.error && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 2 }}>Not despatched: {o.error}</div>}
                  </div>
                  {o.saturday && <Chip bg="#fde68a" fg="#7a4a00">Saturday delivery</Chip>}
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{o.how_sub}</div>
                  {open && !o.despatched && run.friday && o.how === 'DPD' && (
                    <Btn variant="ghost" size="sm" disabled={busy} onClick={() => saturday(o)}>{o.saturday ? 'Not Saturday' : 'Saturday delivery'}</Btn>
                  )}
                  {open && !o.despatched && <Btn variant="ghost" size="sm" disabled={busy} onClick={() => takeOut(o)}>Take out of run</Btn>}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {open && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap', borderTop: '1px solid var(--border)', paddingTop: 16 }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {!left.length ? 'Nothing left in this run' : unpacked.length ? `Tick each order as it's packed — ${unpacked.length} to go` : 'All packed — stock comes off, duty and billing are done as each order goes'}
          </span>
          <Btn disabled={busy || !left.length || unpacked.length > 0} onClick={despatch}>Mark run despatched ({left.length})</Btn>
        </div>
      )}
    </div>
  );
}

const TABS = [['orders', 'Orders'], ['files', 'Order files'], ['email', 'Email inbox'], ['forms', 'Order forms']];

export default function Orders() {
  const [params, setParams] = useSearchParams();
  const runId = params.get('run');
  const tab = params.get('tab') || 'orders';
  const [order, setOrder] = useState(null);
  const [openFileId, setOpenFileId] = useState(null);
  const openRun = (id) => setParams(id ? { run: id } : {});
  const setTab = (t) => setParams(t === 'orders' ? {} : { tab: t });
  const clearOpenFile = useCallback(() => setOpenFileId(null), []);

  if (order?.kind === 'fulfilment') return <FulfilmentOrderDetail orderId={order.id} onBack={() => setOrder(null)} onRefresh={() => {}} clientsById={{}} />;
  if (order?.kind === 'sales') return <SalesOrderDetail orderId={order.id} onBack={() => setOrder(null)} onRefresh={() => {}} />;
  if (runId) return <RunView key={runId} runId={runId} onBack={() => openRun(null)} />;
  return (
    <div>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Orders</h1>
      <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Everything going out — fulfilment, own-brand sales, samples and pallets</p>
      <div style={{ display: 'flex', gap: 4, margin: '16px 0 20px', borderBottom: '1px solid var(--border)' }}>
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', border: 'none', background: 'none', marginBottom: -1, color: tab === k ? 'var(--pld-green)' : 'var(--text-muted)', borderBottom: tab === k ? '2px solid var(--pld-green)' : '2px solid transparent' }}>{label}</button>
        ))}
      </div>
      {tab === 'orders' && <OrdersList onOpenRun={openRun} onOpenOrder={setOrder} />}
      {tab === 'files' && <OrderFilesView openFileId={openFileId} onOpened={clearOpenFile} />}
      {tab === 'forms' && <OrderFormsView onOpenInbox={() => setTab('email')} />}
      {tab === 'email' && <EmailQueueView onOpenImport={(id) => { setOpenFileId(id); setTab('files'); }} />}
    </div>
  );
}
